import { Card, CardContent, Box, Divider, Typography } from '@mui/material'
import { useRouteQuery } from '@/hooks'
import { Button } from '@/@core/components/mui/Button'
import CustomAvatar from '@/@core/components/mui/Avatar'
import { Attendances, Balance, ICompany } from '@/@data'
import { getInitials } from '@/utils/getInitials'
import { CreateBalanceTransaction } from './CreateBalanceTransaction'
import FilterMonthAttendance from '@/components/attendance/detail-attendance/FillterMonthAttendance'

import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { CaslCan } from '@/contexts/CaslProvider'

export const BalanceFilterBarDetail = ({ company, refetch }: { company: any; refetch: () => void }) => {
  const { __ } = useTranslator()
  const { updateQuery, searchQuery } = useRouteQuery()
  const month = searchQuery.get('month') || new Date().toISOString().substring(0, 7)
  const [openCreateBalanceTransaction, setOpenCreateBalanceTransaction] = useState(false)
  const { id } = useParams()

  const getAvatar = (params: Pick<ICompany, 'logo_file' | 'name'>) => {
    const { logo_file, name } = params

    if (logo_file) {
      return <CustomAvatar src={logo_file.tiny_url} skin='light' size={50} />
    } else {
      return (
        <CustomAvatar skin='light' size={50}>
          {getInitials(name as string)}
        </CustomAvatar>
      )
    }
  }

  const isCurrentOrFutureMonth = dayjs(month).isAfter(dayjs(), 'month') || dayjs(month).isSame(dayjs(), 'month')

  const paramsBalance = useMemo(() => {
    return {
      company_id: id,
      year: month.substring(0, 4),
      month: month.substring(5, 7)
    }
  }, [id, month])

  const { data: dataBalanceTransactionDept } = useQuery({
    ...Balance.getBalanceDeptByCompanyId(paramsBalance),
    enabled: !!id && !!month
  })

  const params = useMemo(() => {
    return {
      companyId: Number(id),
      month: searchQuery.get('month')
    }
  }, [id, searchQuery.get('month')])

  const { data } = useQuery({
    ...Attendances.getAttendanceDetailByCompanyId(params),
    enabled: !!id && !!searchQuery.get('month')
  })

  return (
    <Card className='rounded-[16px]'>
      <CardContent className='p-6'>
        <div className='flex justify-between items-center'>
          <Box className='flex gap-2 items-center'>
            {getAvatar({ logo_file: company?.logo_file, name: company?.name || '-' })}
            <div className='flex flex-col'>
              <Typography
                className='font-medium max-w-[200px] whitespace-nowrap overflow-hidden text-ellipsis text-bold'
                color='text.primary'
              >
                {company?.name || '-'}
              </Typography>

              <Typography variant='body2'>{company?.system_code || '-'}</Typography>
            </div>
          </Box>
          <div className='flex flex-row items-center gap-3'>
            <Divider
              orientation='vertical'
              flexItem
              className='h-10'
              sx={{
                borderWidth: '1px',
                borderColor: '#9CA3AF',
                opacity: 0.3
              }}
            />
            <CaslCan action='post' subject='balance_list'>
              <Button
                variant='contained'
                startIcon={<i className='tabler-plus' />}
                disabled={!dataBalanceTransactionDept?.data?.is_allow_create_balance}
                onClick={() => setOpenCreateBalanceTransaction(!openCreateBalanceTransaction)}
              >
                {__('Create Balance Transaction')}
              </Button>
            </CaslCan>
          </div>
        </div>
        <Divider className='my-4' />
        <FilterMonthAttendance
          onDateChange={month => {
            updateQuery({ month })
          }}
          isChooseFuture={true}
          status={
            isCurrentOrFutureMonth
              ? dataBalanceTransactionDept?.data?.status === 'pending' || !dataBalanceTransactionDept?.data?.status
                ? 'estimate balance'
                : dataBalanceTransactionDept?.data?.status
              : dataBalanceTransactionDept?.data?.status
          }
          data={data?.data || {}}
          isShowSettingDay={true}
          showToday={false}
        />
        <CreateBalanceTransaction
          open={openCreateBalanceTransaction}
          handleClose={() => setOpenCreateBalanceTransaction(!openCreateBalanceTransaction)}
          refetch={refetch}
        />
      </CardContent>
    </Card>
  )
}
